import React, { useState } from 'react';

import {
    autoUpdate,
    flip,
    FloatingFocusManager,
    FloatingPortal,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from '@floating-ui/react';

import IconChevronDown from '@pkg/assets/icons/chevron-down.svg?react';
import { type CommonActionProps } from '@pkg/primitives';

import {
    MenuButton,
    MenuInside,
    MenuWrapper,
    TriggerAction,
    TriggerActionIcon,
    TriggerActionLabel,
} from './FilterDropdown.style';

export interface FilterDropdownOption {
    id: string;
    label: string;
    onAction?: (id: string) => void;
}

export interface FilterDropdownProps {
    options?: FilterDropdownOption[];
    selectedId?: FilterDropdownOption['id'];
    alignLeft?: boolean;
    onSelectOption?: (id: FilterDropdownOption['id']) => void;
}

const NO_OPTIONS_LABEL = 'Select a filter…';

export function FilterDropdown({ options = [], selectedId, alignLeft = false, onSelectOption }: FilterDropdownProps) {
    const [open, onOpenChange] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange,
        placement: alignLeft ? 'bottom-start' : 'bottom-end',
        middleware: [offset(10), flip({ fallbackAxisSideDirection: 'end' }), shift()],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    const { onClick } = getReferenceProps();

    const handleClick: CommonActionProps['onAction'] = (event) => {
        if (typeof onClick === 'function') onClick(event);
    };

    function handleSelect(id: FilterDropdownOption['id']) {
        onOpenChange(false);
        onSelectOption?.(id);
    }

    const selectedLabel = options.find(({ id }) => id === selectedId)?.label ?? NO_OPTIONS_LABEL;

    // TODO: Pass `disabled` and `ariaLabel` to <TriggerAction />.
    return (
        <>
            <TriggerAction ref={refs.setReference} {...getReferenceProps()} pressed={open} onAction={handleClick}>
                <TriggerActionLabel>{selectedLabel}</TriggerActionLabel>

                <TriggerActionIcon>
                    <IconChevronDown />
                </TriggerActionIcon>
            </TriggerAction>

            {open && (
                <FloatingPortal>
                    <FloatingFocusManager context={context} modal={false}>
                        <MenuWrapper ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
                            <MenuInside
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                            >
                                {options.map(({ id, label, onAction }) => (
                                    <MenuButton
                                        key={`FilterDropdown-Option-${id}`}
                                        onClick={() => {
                                            handleSelect(id);
                                            onAction?.(id);
                                        }}
                                    >
                                        {label}
                                    </MenuButton>
                                ))}
                            </MenuInside>
                        </MenuWrapper>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </>
    );
}
